import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Pursuits from './Pursuits';
import { useAuth } from '../../hooks/useAuth';
import { pipelineApi } from '../../services/api';
import { formatAgency } from '../../utils/agency';
import WatchCriteriaModal from '../../components/pipeline/WatchCriteriaModal';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  UserCircleIcon,
  SparklesIcon,
  AdjustmentsHorizontalIcon,
  TrashIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from 'components/icons';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import PageHeader from '../../components/ui/PageHeader';

const PIPELINE_STAGES = [
  { id: 'new', name: 'New', order: 1, color: 'bg-surface-sunk', borderColor: 'border-border', textColor: 'text-content' },
  { id: 'analyzing', name: 'Analyzing', order: 2, color: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-700' },
  { id: 'qualified', name: 'Qualified', order: 3, color: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-700' },
  { id: 'proposal', name: 'Proposal', order: 4, color: 'bg-purple-50', borderColor: 'border-purple-200', textColor: 'text-purple-700' },
  { id: 'submitted', name: 'Submitted', order: 5, color: 'bg-orange-50', borderColor: 'border-orange-200', textColor: 'text-orange-700' },
  { id: 'awarded', name: 'Awarded', order: 6, color: 'bg-emerald-50', borderColor: 'border-emerald-200', textColor: 'text-emerald-700' },
  { id: 'lost', name: 'Lost', order: 7, color: 'bg-red-50', borderColor: 'border-red-200', textColor: 'text-red-700' },
  { id: 'archived', name: 'Archived', order: 8, color: 'bg-slate-50', borderColor: 'border-slate-200', textColor: 'text-slate-700' },
];

const TERMINAL_STAGES = ['awarded', 'lost'];
interface PipelineOpportunity {
  id: string;
  opportunityId: string;
  stage: string;
  matchScore: number;
  notes?: string;
  opportunity?: {
    id: string;
    samNoticeId: string;
    title: string;
    agency?: string;
    naicsCode?: string;
    naicsCodes?: string[];
    responseDeadline?: string;
    setAside?: string;
    solicitationNumber?: string;
  };
  metadata?: { comments?: number; archivedReason?: string; archivedFrom?: string; archivedAt?: string };
  createdAt: string;
}

const Pipeline: React.FC = () => {
  const { user } = useAuth();
  const isFreePlan = user?.plan === 'FREE';
  // One page, two layouts. The list is the default: most contractors want
  // what is due, not which column it sits in. `?view=` wins over the
  // remembered choice so links from the dashboard land where they say.
  const [params, setParams] = useSearchParams();
  const paramView = params.get('view');
  const [layout, setLayoutState] = useState<'list' | 'board'>(() => {
    if (paramView === 'board' || paramView === 'list') return paramView;
    try { return localStorage.getItem('clevor.pursuits.layout') === 'board' ? 'board' : 'list'; } catch { return 'list'; }
  });
  const setLayout = (l: 'list' | 'board') => {
    setLayoutState(l);
    try { localStorage.setItem('clevor.pursuits.layout', l); } catch { /* ignore */ }
    const next = new URLSearchParams(params); next.set('view', l); if (l === 'board') next.delete('filter'); setParams(next, { replace: true });
  };
  useEffect(() => { if ((paramView === 'board' || paramView === 'list') && paramView !== layout) setLayoutState(paramView); }, [paramView]); // eslint-disable-line react-hooks/exhaustive-deps
  const [opportunities, setOpportunities] = useState<PipelineOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // Past-deadline cards stay in the stage the user put them in, but fold
  // under the live ones so a column of expired work does not read as a
  // column of work. Open per column.
  const [showPastIn, setShowPastIn] = useState<Record<string, boolean>>({});
  const isPast = (opp: PipelineOpportunity) => {
    if (TERMINAL_STAGES.includes(opp.stage) || opp.stage === 'archived') return false;
    const d = opp.opportunity?.responseDeadline ? new Date(opp.opportunity.responseDeadline).getTime() : NaN;
    return !Number.isNaN(d) && d < Date.now();
  };
  const columnRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Stage transition dialog
  const [stageDialog, setStageDialog] = useState<{
    oppId: string;
    oppTitle: string;
    fromStage: string;
    toStage: string;
    type: 'terminal' | 'backward' | null;
  } | null>(null);
  const [stageNote, setStageNote] = useState('');


  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const response = await pipelineApi.getOpportunities();
      const pipelineData = response.data.data?.opportunities || response.data.opportunities || [];
      setOpportunities(pipelineData);
    } catch {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchPipeline();
  }, []);

  const getStageOrder = (stageId: string) => PIPELINE_STAGES.find(s => s.id === stageId)?.order || 0;

  const requestStageChange = (oppId: string, newStage: string) => {
    const opp = opportunities.find(o => o.id === oppId);
    if (!opp) return;

    const fromOrder = getStageOrder(opp.stage);
    const toOrder = getStageOrder(newStage);
    const oppTitle = opp.opportunity?.title || 'This opportunity';

    // Terminal stages (awarded/lost) always need confirmation
    if (TERMINAL_STAGES.includes(newStage)) {
      setStageDialog({ oppId, oppTitle, fromStage: opp.stage, toStage: newStage, type: 'terminal' });
      setStageNote('');
      return;
    }

    // Moving backward needs confirmation
    if (toOrder < fromOrder && fromOrder - toOrder > 1) {
      setStageDialog({ oppId, oppTitle, fromStage: opp.stage, toStage: newStage, type: 'backward' });
      setStageNote('');
      return;
    }

    // Forward movement or small step back — proceed directly
    executeStageChange(oppId, newStage);
  };

  const executeStageChange = async (oppId: string, newStage: string, notes?: string) => {
    try {
      await pipelineApi.updateStage(oppId, { stage: newStage, notes });
      toast.success(`Moved to ${PIPELINE_STAGES.find(s => s.id === newStage)?.name}`);
      fetchPipeline();
    } catch (e: any) {
      if (e?.response?.status === 409 && e.response.data?.data?.blockers) {
        const b = e.response.data.data.blockers as Array<{ title: string }>;
        toast.error(`${b.length} thing${b.length === 1 ? '' : 's'} block${b.length === 1 ? 's' : ''} Submitted: ${b.map((x) => x.title).join('; ')}. Open the pursuit to resolve or override.`, { duration: 9000 });
      } else {
        toast.error(e?.response?.data?.error?.message || 'Failed to update stage');
      }
    }
    setStageDialog(null);
    setStageNote('');
  };

  const handleRemove = async (pipelineId: string) => {
    try {
      await pipelineApi.remove(pipelineId);
      setOpportunities(prev => prev.filter(o => o.id !== pipelineId));
      setConfirmDeleteId(null);
      toast.success('Removed from pipeline');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleAnalyze = async (oppId: string) => {
    try {
      toast.loading('Analyzing...', { id: `analyze-${oppId}` });
      // This endpoint moves to "analyzing" stage and queues the agent
      await pipelineApi.analyze(oppId);
      toast.success('Analysis started — will move to Qualified when complete', { id: `analyze-${oppId}` });
      fetchPipeline();
    } catch {
      toast.error('Analysis failed', { id: `analyze-${oppId}` });
    }
  };

  const handleMatchVendors = async (oppId: string, samNoticeId?: string) => {
    try {
      toast.loading('Finding vendors...', { id: `match-${oppId}` });
      // matchVendors endpoint expects an opportunity ID (samNoticeId), not pipeline entry ID
      await pipelineApi.matchVendors(samNoticeId || oppId);
      toast.success('Vendor matching queued', { id: `match-${oppId}` });
    } catch {
      toast.error('Vendor matching failed', { id: `match-${oppId}` });
    }
  };

  const dueSoonCount = opportunities.filter(o => {
    if (TERMINAL_STAGES.includes(o.stage) || o.stage === 'archived') return false;
    const d = o.opportunity?.responseDeadline ? new Date(o.opportunity.responseDeadline).getTime() : NaN;
    return !Number.isNaN(d) && d >= Date.now() && d - Date.now() <= 7 * 86400000;
  }).length;

  const getStageOpportunities = (stageId: string) => {
    return opportunities
      .filter(opp => opp.stage === stageId)
      .sort((a, b) => b.matchScore - a.matchScore);
  };

  const timeLeft = (deadline?: string) => {
    if (!deadline) return 'N/A';
    const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Expired';
    if (days === 0) return 'Due today';
    if (days === 1) return '1 day left';
    if (days <= 7) return `${days} day${days === 1 ? '' : 's'} left`;
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} left`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  // Stage-specific actions for each card
  const renderCardActions = (opp: PipelineOpportunity, stageId: string) => {
    return (
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
        {stageId === 'new' && (
          <button onClick={() => handleAnalyze(opp.id)} className="flex-1 flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800">
            <SparklesIcon className="h-3 w-3" /> Analyze
          </button>
        )}
        {stageId === 'qualified' && (
          <button onClick={() => handleMatchVendors(opp.id, opp.opportunity?.samNoticeId)} className="flex-1 flex items-center justify-center gap-1 text-xs text-purple-600 hover:text-purple-800">
            <UserCircleIcon className="h-3 w-3" /> Find Partners
          </button>
        )}
        {stageId === 'proposal' && (
          <Link to={`/proposal-writer?opportunityId=${opp.opportunity?.samNoticeId || opp.opportunityId}`} className="flex-1 flex items-center justify-center gap-1 text-xs text-purple-600 hover:text-purple-800">
            <DocumentTextIcon className="h-3 w-3" /> Write Proposal
          </Link>
        )}
        {stageId === 'submitted' && (
          <span className="flex-1 text-center text-xs text-content-subtle">Awaiting result</span>
        )}
        {(stageId === 'awarded') && (
          <span className="flex-1 text-center text-xs text-emerald-600 font-medium">Won</span>
        )}
        {(stageId === 'lost') && (
          <span className="flex-1 text-center text-xs text-red-500 font-medium">Lost</span>
        )}
        {stageId === 'archived' && opp.metadata?.archivedReason === 'deadline_passed' && (
          <button
            type="button"
            onClick={() => executeStageChange(opp.id, opp.metadata?.archivedFrom || 'new')}
            title={`Archived automatically: the deadline passed while this was in ${PIPELINE_STAGES.find((s) => s.id === opp.metadata?.archivedFrom)?.name || 'an early stage'}. Restore puts it back there and keeps it there.`}
            className="flex-1 flex items-center justify-center gap-1 text-xs text-content-muted hover:text-content"
          >
            <ArrowUturnLeftIcon className="h-3 w-3" /> Restore to {PIPELINE_STAGES.find((s) => s.id === opp.metadata?.archivedFrom)?.name || 'New'}
          </button>
        )}
        <Link to={`/pursuits/${opp.id}`} className="flex-1 text-center text-xs text-content-muted hover:text-content">
          Workspace
        </Link>
      </div>
    );
  };

  const renderStageColumn = (stage: typeof PIPELINE_STAGES[0]) => {
    let stageOpps = getStageOpportunities(stage.id);
    // Free users see up to 5 most recent entries total (limit per-stage display)
    if (isFreePlan) {
      stageOpps = stageOpps.slice(0, 3);
    }
    const canDrag = !isFreePlan;
    const pastOpps = stageOpps.filter(isPast);
    const liveOpps = stageOpps.filter((o) => !isPast(o));
    const pastOpen = !!showPastIn[stage.id];
    const visibleOpps = pastOpen ? [...liveOpps, ...pastOpps] : liveOpps;
    return (
      <div
        key={stage.id}
        ref={(el) => { columnRefs.current[stage.id] = el; }}
        className={`${stage.color} rounded-lg border ${stage.borderColor} min-h-[300px] w-72 flex-shrink-0 snap-start flex flex-col`}
        onDragOver={(e) => { if (canDrag) e.preventDefault(); }}
        onDrop={(e) => {
          if (!canDrag) return;
          e.preventDefault();
          const oppId = e.dataTransfer.getData('oppId');
          if (oppId) requestStageChange(oppId, stage.id);
          setDraggingId(null);
        }}
      >
        <div className={`p-3 border-b ${stage.borderColor} flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <h3 className={`font-semibold ${stage.textColor}`}>{stage.name}</h3>
            <span className="bg-surface px-2 py-1 rounded-full text-xs font-medium text-content-muted" title={pastOpps.length ? `${liveOpps.length} open, ${pastOpps.length} past deadline` : undefined}>{liveOpps.length}{pastOpps.length > 0 && <span className="text-content-subtle"> +{pastOpps.length}</span>}</span>
          </div>
        </div>
        <div className="p-3 space-y-3 flex-1">
          {stageOpps.length === 0 && (
            <p className="text-xs text-content-subtle text-center pt-10">{canDrag ? 'Drag a card here' : 'Empty'}</p>
          )}
          {pastOpps.length > 0 && (
            <button type="button" onClick={() => setShowPastIn((m) => ({ ...m, [stage.id]: !m[stage.id] }))} className="flex w-full items-center justify-between rounded-md border border-dashed border-border px-3 py-2 text-xs text-content-muted hover:bg-surface hover:text-content">
              <span>{pastOpps.length} past deadline</span><span>{pastOpen ? 'Hide' : 'Show'}</span>
            </button>
          )}
          {visibleOpps.map(opp => (
            <div
              key={opp.id}
              draggable={canDrag}
              onDragStart={(e) => { if (!canDrag) return; e.dataTransfer.setData('oppId', opp.id); setDraggingId(opp.id); }}
              onDragEnd={() => setDraggingId(null)}
              className={`bg-surface rounded-lg p-4 shadow-sm border border-border ${canDrag ? 'cursor-move' : 'cursor-default'} hover:shadow-md transition-shadow ${draggingId === opp.id ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                {opp.matchScore > 0 ? (
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${getScoreColor(Math.round(opp.matchScore * 100))}`} />
                    <span className="text-xs font-medium text-content-muted">{Math.round(opp.matchScore * 100)}% match</span>
                  </div>
                ) : <span />}
                {confirmDeleteId === opp.id ? (
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleRemove(opp.id)} className="text-xs text-white bg-red-500 hover:bg-red-600 px-2 py-0.5 rounded">Remove</button>
                    <button onClick={() => setConfirmDeleteId(null)} className="text-xs text-content-muted hover:text-content">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDeleteId(opp.id)} className="p-1 text-content-subtle hover:text-red-500 rounded" title="Remove from pipeline">
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <Link
                to={`/pursuits/${opp.id}`}
                className="text-sm font-medium text-brand-600 hover:text-brand-800 line-clamp-2 mb-2 block"
              >
                {opp.opportunity?.title || 'Untitled Opportunity'}
              </Link>

              <div className="space-y-1 text-xs text-content-muted">
                <p className="truncate">{formatAgency(opp.opportunity?.agency)}</p>
                <div className="flex items-center justify-between">
                  {opp.opportunity?.naicsCode ? <span className="font-mono bg-surface-sunk px-1.5 py-0.5 rounded">{opp.opportunity.naicsCode}</span> : <span />}
                  {opp.opportunity?.setAside && (
                    <span className="text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">{opp.opportunity?.setAside}</span>
                  )}
                </div>
                <p className={`font-medium ${
                  timeLeft(opp.opportunity?.responseDeadline).includes('Expired') ? 'text-red-600' :
                  timeLeft(opp.opportunity?.responseDeadline).includes('today') ? 'text-orange-600' : 'text-green-600'
                }`}>
                  {timeLeft(opp.opportunity?.responseDeadline)}
                </p>
                {stage.id === 'archived' && opp.metadata?.archivedReason === 'deadline_passed' && (
                  <p className="text-content-subtle">Archived: deadline passed while in {PIPELINE_STAGES.find((s) => s.id === opp.metadata?.archivedFrom)?.name || 'an early stage'}</p>
                )}
              </div>

              {renderCardActions(opp, stage.id)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // One editor for what is watched, shared with the New matches panel.
  const watchModal = <WatchCriteriaModal open={showConfigModal} onClose={() => setShowConfigModal(false)} />;

  const layoutControl = (
    <div role="group" aria-label="Layout" className="flex overflow-hidden rounded-token border border-border text-label">
      {(['list', 'board'] as const).map((l) => (
        <button key={l} type="button" aria-pressed={layout === l} onClick={() => setLayout(l)} className={`min-h-[40px] px-3.5 ${layout === l ? 'bg-accent-sunk font-semibold text-accent' : 'text-content-muted hover:text-content'}`}>{l === 'list' ? 'List' : 'Board'}</button>
      ))}
    </div>
  );

  const header = (
    <PageHeader
      title="Pursuits"
      subtitle={layout === 'list' ? 'Every solicitation you are pursuing, nearest obligation first.' : 'Move pursuits through the stages as your bid progresses.'}
      actions={
        <>
          {layoutControl}
          {!isFreePlan && (
            <button type="button" onClick={() => setShowConfigModal(true)} className="inline-flex min-h-[40px] items-center gap-2 rounded-token border border-border-strong bg-surface px-3.5 text-label font-medium text-content hover:bg-surface-sunk">
              <AdjustmentsHorizontalIcon className="h-4 w-4" /> What I'm watching
            </button>
          )}
          {!isFreePlan && layout === 'board' && (
            <button type="button" onClick={fetchPipeline} aria-label="Refresh" className="inline-flex min-h-[40px] items-center rounded-token border border-border-strong bg-surface px-3 text-content hover:bg-surface-sunk">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </>
      }
    />
  );

  if (layout === 'list') {
    return (
      <div className="space-y-6">
        {header}
        <Pursuits embedded />
        {watchModal}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {header}
        <div className="flex items-center justify-center h-64">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-brand-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

      {isFreePlan && (
        <div className="bg-brand-50 border border-brand-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-brand-700">
            You're viewing a limited pipeline. Upgrade for stage automation, drag-and-drop management, and unlimited entries.
          </span>
          <Link to="/pricing" className="text-sm font-medium text-brand-600 hover:text-brand-500 whitespace-nowrap">
            Upgrade &rarr;
          </Link>
        </div>
      )}

      {/* Stages are the user's judgement about their own bid, so the product
          does not reorganise the board under them. Say so plainly, and name the
          single exception — otherwise the first card that auto-promotes after
          Analyze makes this note look wrong. */}
      <p className="flex items-start gap-1.5 text-xs text-content-muted">
        <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-px" aria-hidden="true" />
        <span>
          Cards move when you move them — deciding an opportunity is worth pursuing is your call,
          not ours. The one exception: running <span className="font-medium text-content">Analyze</span>{' '}
          moves a new opportunity to <span className="font-medium text-content">Qualified</span> when
          it finishes, and returns it if it fails.
        </span>
      </p>

      {/* One strip instead of eight tiles plus a slider: the counts live in the
          column headers, and the strip's job is orientation — tap a stage and
          the board scrolls to it. */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="navigation" aria-label="Stages">
        <span className="text-sm text-content-muted whitespace-nowrap mr-1">
          {opportunities.length === 0 ? 'No pursuits yet' : `${opportunities.length} pursuit${opportunities.length === 1 ? '' : 's'}`}
          {opportunities.filter(isPast).length > 0 && <> · <span>{opportunities.filter(isPast).length} past deadline</span></>}
          {dueSoonCount > 0 && <> · <span className="text-warning font-medium">{dueSoonCount} due within 7 days</span></>}
        </span>
        {PIPELINE_STAGES.map(stage => {
          const n = getStageOpportunities(stage.id).filter((o) => !isPast(o)).length;
          return (
            <button
              key={stage.id}
              type="button"
              onClick={() => columnRefs.current[stage.id]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs ${n ? `${stage.borderColor} ${stage.color} ${stage.textColor} font-medium` : 'border-border text-content-subtle'}`}
            >
              {stage.name}<span className="tabular-nums">{n}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-surface rounded-lg shadow overflow-hidden">
        <div className="p-4">
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 scroll-px-4">
            {PIPELINE_STAGES.map(stage => renderStageColumn(stage))}
          </div>
        </div>
      </div>

      {/* Stage Transition Confirmation Dialog */}
      {/* Overlay dismissal is off: marking awarded/lost moves win-rate
          analytics, so a stray backdrop click shouldn't discard the note.
          Escape still closes — a keyboard user is never trapped. */}
      <Modal
        open={!!stageDialog}
        onClose={() => { setStageDialog(null); setStageNote(''); }}
        dismissOnOverlayClick={false}
        icon={ExclamationTriangleIcon}
        size="sm"
        title={
          stageDialog
            ? stageDialog.type === 'terminal'
              ? `Mark as ${PIPELINE_STAGES.find(s => s.id === stageDialog.toStage)?.name}?`
              : 'Move backward?'
            : ''
        }
        description={
          stageDialog
            ? stageDialog.type === 'terminal'
              ? `This marks the opportunity as ${stageDialog.toStage === 'awarded' ? 'won' : 'lost'} and affects your win rate analytics.`
              : `Moving from "${PIPELINE_STAGES.find(s => s.id === stageDialog.fromStage)?.name}" back to "${PIPELINE_STAGES.find(s => s.id === stageDialog.toStage)?.name}".`
            : undefined
        }
        footer={
          stageDialog ? (
            <>
              <button
                onClick={() => { setStageDialog(null); setStageNote(''); }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => executeStageChange(stageDialog.oppId, stageDialog.toStage, stageNote || undefined)}
                className={
                  stageDialog.toStage === 'lost'
                    ? 'btn-danger'
                    : 'btn-primary'
                }
              >
                {stageDialog.toStage === 'awarded' ? 'Mark as Awarded' :
                 stageDialog.toStage === 'lost' ? 'Mark as Lost' :
                 'Confirm Move'}
              </button>
            </>
          ) : null
        }
      >
        {stageDialog && (
          <>
            <p className="text-body font-semibold text-content truncate">{stageDialog.oppTitle}</p>
            <div className="mt-4">
              <label htmlFor="stage-note" className="label mb-1">
                {stageDialog.toStage === 'lost' ? 'Why was this lost? (optional)' :
                 stageDialog.toStage === 'awarded' ? 'Notes (optional)' :
                 'Reason for moving back (optional)'}
              </label>
              <textarea
                id="stage-note"
                value={stageNote}
                onChange={(e) => setStageNote(e.target.value)}
                rows={3}
                className="input"
                placeholder={stageDialog.toStage === 'lost' ? 'e.g. Price too high, missed deadline, went with incumbent…' :
                             stageDialog.toStage === 'awarded' ? 'e.g. Contract number, start date…' :
                             'e.g. Needs more analysis, requirements changed…'}
              />
            </div>
          </>
        )}
      </Modal>

      {watchModal}
    </div>
  );
};

export default Pipeline;
